export type InspectionType = 'new' | 'extension';

export type YesNo = '' | 'yes' | 'no';

export type SoilSuitability = '' | 'highly' | 'moderately' | 'not';

export type CultivarSuitability = '' | 'suitable' | 'not_suitable';

export type CultivarSuitabilityRow = {
  cultivarId: string;
  cultivarName: string;
  suitability: CultivarSuitability;
};

export type PreviousSeasonPlants = {
  totalNumber: string;
  certifiedNumber: string;
  numberOfSales: string;
  remainingCount: string;
};

export type LandSizeDetails = {
  acre: string;
  rood: string;
  perch: string;
  hectare: string;
  numberOfPlants: string;
};

export type CN03Draft = {
  /** Human-readable form ID for display only. */
  refId?: string;
  planterId: string;
  cn01Id: string;
  applicantName: string;
  applicantNic: string;
  inspectionType: InspectionType | '';
  permitNo: string;
  permitSelected: boolean;
  previousSeasonPlants: PreviousSeasonPlants;
  teaNurseryName: string;

  districtId: string;
  tiRange: string;
  elevation: string;
  q06WaterSource: YesNo;
  q07LocationSuitable: YesNo;
  q08SoilFumigation: YesNo;
  q09SoilPhTesting: YesNo;
  soilPh: string;
  q11SoilSuitability: SoilSuitability;
  q12CultivarsRecommended: YesNo;
  cultivarRows: CultivarSuitabilityRow[];
  q14AdequateTraining: YesNo;
  recommendedEstablish: YesNo;
  landSizeDetails: LandSizeDetails;
  notRecommendedReasonId: string;
  otherMatters: string;

  inspectorName: string;
  inspectionDate: string;
  investigatingOfficerName: string;
  investigatingOfficerRole: string;
  completionDate: string;
  completionTime: string;
};

function formatColomboDate(date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Colombo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function formatColomboTime(date = new Date()): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Colombo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export const defaultDraft = (): CN03Draft => {
  const now = new Date();
  return {
    planterId: '',
    cn01Id: '',
    applicantName: '',
    applicantNic: '',
    inspectionType: '',
    permitNo: '',
    permitSelected: false,
    previousSeasonPlants: {
      totalNumber: '',
      certifiedNumber: '',
      numberOfSales: '',
      remainingCount: '',
    },
    teaNurseryName: '',

    districtId: '',
    tiRange: '',
    elevation: '',
    q06WaterSource: '',
    q07LocationSuitable: '',
    q08SoilFumigation: '',
    q09SoilPhTesting: '',
    soilPh: '',
    q11SoilSuitability: 'highly',
    q12CultivarsRecommended: '',
    cultivarRows: [],
    q14AdequateTraining: '',
    recommendedEstablish: '',
    landSizeDetails: {
      acre: '',
      rood: '',
      perch: '',
      hectare: '',
      numberOfPlants: '',
    },
    notRecommendedReasonId: '',
    otherMatters: '',

    inspectorName: '',
    inspectionDate: formatColomboDate(now),
    investigatingOfficerName: '',
    investigatingOfficerRole: '',
    completionDate: formatColomboDate(now),
    completionTime: formatColomboTime(now),
  };
};

/** Form completion display fields from API `createdAt` (Asia/Colombo). */
export function completionFromCreatedAt(createdAt?: string | null): {
  completionDate: string;
  completionTime: string;
} {
  const parsed = createdAt ? new Date(createdAt) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  return {
    completionDate: formatColomboDate(date),
    completionTime: formatColomboTime(date),
  };
}

export { formatColomboDate, formatColomboTime };
